import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/atoms/Avatar';
import { Icon } from '@/design-system/icons/Icon';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';

type Props = { name: string; meta: string; avatar: string; bg?: string };

export const UserCard = ({ name, meta, avatar, bg }: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const isAuthed = useAuthStore((s) => !!s.token);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    await logout();
    toast('Sessão encerrada', 'lock');
    navigate('/auth/login');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-card"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Avatar initials={avatar} bg={bg} color="var(--primary-ink)" size={32} />
        <span className="info">
          <span className="nm">{name}</span>
          <span className="meta">{meta}</span>
        </span>
        <Icon name="chevdown" size={14} className="chev" />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-3)',
            boxShadow: 'var(--sh-3)',
            padding: 6,
            zIndex: 50,
          }}
        >
          <MenuItem icon="user" label="Minha conta" onClick={() => { setOpen(false); navigate('/account'); }} />
          <MenuItem icon="cog" label="Configurações" onClick={() => { setOpen(false); navigate('/account'); }} />
          <MenuItem icon="doc" label="Termos & legal" onClick={() => { setOpen(false); navigate('/legal'); }} />
          {isAuthed && <MenuItem icon="lock" label="Sair" onClick={onLogout} danger />}
          {!isAuthed && <MenuItem icon="key" label="Entrar" onClick={() => { setOpen(false); navigate('/auth/login'); }} />}
        </div>
      )}
    </div>
  );
};

const MenuItem = ({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: 'user' | 'cog' | 'doc' | 'lock' | 'key';
  label: string;
  onClick: () => void;
  danger?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      textAlign: 'left',
      background: 'transparent',
      border: 0,
      padding: '9px 10px',
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      borderRadius: 'var(--r-2)',
      cursor: 'pointer',
      color: danger ? 'var(--danger)' : 'var(--ink)',
      fontSize: 14,
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
  >
    <Icon name={icon} size={16} />
    <span>{label}</span>
  </button>
);
